import { useState, useEffect, useCallback } from "react";
import { Bell, BellRing, X, Trash2, TrendingUp, TrendingDown, Plus, Target, Clock, ArrowRight, Search, Crosshair, CheckCircle, Flame, ShoppingCart } from "lucide-react";
import { priceAlertsAPI } from "../services/api";

function DistanceBar({ pct }) {
  if (pct == null) return <span className="text-[10px] text-slate-600">--</span>;
  const absPct = Math.abs(pct);
  const width = Math.min(100, Math.max(5, 100 - absPct * 10));
  const isClose = absPct < 3;
  const isMedium = absPct < 8;
  const barColor = isClose ? "bg-emerald-400" : isMedium ? "bg-amber-400" : "bg-slate-500";
  const textColor = isClose ? "text-emerald-400 font-black" : isMedium ? "text-amber-400 font-bold" : "text-slate-400";

  return (
    <div className="flex items-center gap-2 w-full">
      <div className="flex-1 h-1.5 bg-slate-700/50 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${width}%` }} />
      </div>
      <span className={`text-[11px] font-mono whitespace-nowrap ${textColor}`}>
        {pct > 0 ? `-${absPct.toFixed(1)}%` : `${absPct.toFixed(1)}%`}
      </span>
    </div>
  );
}

function TimeAgo({ iso }) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return <span className="text-[9px] text-slate-600">maintenant</span>;
    if (diff < 3600) return <span className="text-[9px] text-slate-600">{Math.floor(diff / 60)}min</span>;
    if (diff < 86400) return <span className="text-[9px] text-slate-600">{Math.floor(diff / 3600)}h</span>;
    return <span className="text-[9px] text-slate-600">{Math.floor(diff / 86400)}j</span>;
  } catch { return null; }
}

export default function AlertsPanel({ owner, onClose, onAnalyze, getTickerCurrency }) {
  const [data, setData] = useState(null);
  const [hotAlerts, setHotAlerts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quickTicker, setQuickTicker] = useState("");
  const [quickPrice, setQuickPrice] = useState("");
  const [quickNote, setQuickNote] = useState("");
  const [creating, setCreating] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);

  const curr = useCallback((ticker) => getTickerCurrency?.(ticker) || "$", [getTickerCurrency]);

  const fetchData = useCallback(async () => {
    try {
      // Load hot alerts FIRST (fast), then enriched (slower)
      priceAlertsAPI.getHot(owner).then(h => setHotAlerts(h)).catch(() => {});
      const enriched = await priceAlertsAPI.getEnriched(owner);
      setData(enriched);
    } catch (e) {
      console.error("Alerts error:", e);
    } finally {
      setLoading(false);
    }
  }, [owner]);

  useEffect(() => {
    fetchData();
    const iv = setInterval(fetchData, 60000);
    return () => clearInterval(iv);
  }, [fetchData]);

  const handleRemove = async (ticker) => {
    try {
      await priceAlertsAPI.remove(owner, ticker);
      setData(prev => prev ? { ...prev, active: prev.active.filter(a => a.ticker !== ticker) } : prev);
    } catch (e) { console.error(e); }
  };

  const handleDismiss = async (alert) => {
    try {
      await priceAlertsAPI.dismiss(alert.ticker, owner, alert.created_at);
      setData(prev => prev ? { ...prev, triggered: prev.triggered.filter(a => a.created_at !== alert.created_at || a.ticker !== alert.ticker) } : prev);
    } catch (e) { console.error(e); }
  };

  const handleQuickCreate = async () => {
    if (!quickTicker.trim() || !quickPrice) return;
    setCreating(true);
    try {
      await priceAlertsAPI.create({
        ticker: quickTicker.trim().toUpperCase(),
        target_price: parseFloat(quickPrice),
        direction: "below",
        owner,
        signal: "CUSTOM",
        note: quickNote || `Alerte manuelle ${quickTicker.toUpperCase()} @ ${quickPrice}`,
      });
      setQuickTicker("");
      setQuickPrice("");
      setQuickNote("");
      setShowQuickAdd(false);
      fetchData();
    } catch (e) { console.error(e); }
    setCreating(false);
  };

  const active = data?.active || [];
  const triggered = data?.triggered || [];
  const stats = data?.stats || {};

  // Split active alerts into "close" (< 5%) and "surveillance" (>= 5%)
  const closeAlerts = active.filter(a => a.distance_pct != null && Math.abs(a.distance_pct) < 5);
  const surveillanceAlerts = active.filter(a => a.distance_pct == null || Math.abs(a.distance_pct) >= 5);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="alerts-panel-overlay">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div className="relative w-full max-w-md h-full bg-slate-900 border-l border-slate-700/60 shadow-2xl flex flex-col animate-in slide-in-from-right" data-testid="alerts-panel">
        {/* Header */}
        <div className="p-4 border-b border-slate-700/50 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-amber-500/15 border border-amber-500/30">
              <Bell className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h2 className="text-sm font-black text-white tracking-tight">Opportunites d'Achat</h2>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                {triggered.length > 0 && (
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-500/15 text-emerald-300 border border-emerald-500/20" data-testid="triggered-count-badge">
                    {triggered.length} prix atteint{triggered.length > 1 ? "s" : ""}
                  </span>
                )}
                {closeAlerts.length > 0 && (
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-500/15 text-amber-300 border border-amber-500/20" data-testid="close-count-badge">
                    {closeAlerts.length} proche{closeAlerts.length > 1 ? "s" : ""}
                  </span>
                )}
                {surveillanceAlerts.length > 0 && (
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-slate-500/15 text-slate-400 border border-slate-600/20">
                    {surveillanceAlerts.length} en veille
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button onClick={() => setShowQuickAdd(!showQuickAdd)} className="p-2 rounded-lg bg-emerald-500/15 border border-emerald-500/25 text-emerald-300 hover:bg-emerald-500/25 transition-colors" data-testid="quick-add-alert-btn" title="Nouvelle alerte">
              <Plus className="w-4 h-4" />
            </button>
            <button onClick={onClose} className="p-2 hover:bg-slate-700/60 rounded-lg transition-colors" data-testid="close-alerts-panel">
              <X className="w-4 h-4 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Quick Add Form */}
        {showQuickAdd && (
          <div className="p-3 border-b border-slate-700/50 bg-slate-800/40 space-y-2" data-testid="quick-add-form">
            <div className="flex items-center gap-2">
              <input type="text" value={quickTicker} onChange={e => setQuickTicker(e.target.value.toUpperCase())} placeholder="AAPL" className="flex-1 px-2.5 py-1.5 rounded-lg bg-slate-800 border border-slate-600/50 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-amber-500/50 font-mono" data-testid="quick-ticker-input" />
              <input type="number" step="0.01" value={quickPrice} onChange={e => setQuickPrice(e.target.value)} placeholder="Prix cible" className="w-28 px-2.5 py-1.5 rounded-lg bg-slate-800 border border-slate-600/50 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-amber-500/50 font-mono" data-testid="quick-price-input" />
            </div>
            <div className="flex items-center gap-2">
              <input type="text" value={quickNote} onChange={e => setQuickNote(e.target.value)} placeholder="Note (optionnel)" className="flex-1 px-2.5 py-1.5 rounded-lg bg-slate-800 border border-slate-600/50 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-amber-500/50" data-testid="quick-note-input" onKeyDown={e => e.key === "Enter" && handleQuickCreate()} />
              <button onClick={handleQuickCreate} disabled={creating || !quickTicker || !quickPrice} className="px-3 py-1.5 rounded-lg bg-amber-500/20 border border-amber-500/30 text-amber-300 text-xs font-bold hover:bg-amber-500/30 transition-colors disabled:opacity-30" data-testid="quick-create-btn">
                {creating ? "..." : "Creer"}
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3 space-y-4">
          {loading && !hotAlerts ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-6 h-6 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* ============================================ */}
              {/* HOT ALERTS RANKING                           */}
              {/* ============================================ */}
              {hotAlerts && hotAlerts.hot?.length > 0 && (
                <div data-testid="hot-alerts-section">
                  <div className="flex items-center gap-2 mb-3 p-2 rounded-lg bg-gradient-to-r from-red-500/10 via-amber-500/10 to-cyan-500/10 border border-slate-700/30">
                    <Flame className="w-4 h-4 text-amber-400" />
                    <span className="text-xs font-black text-white uppercase tracking-wider">Hot Alerts</span>
                    <div className="ml-auto flex gap-1.5">
                      {hotAlerts.counts?.urgent > 0 && <span className="px-1.5 py-0.5 rounded text-[8px] font-black bg-red-500/20 text-red-300 border border-red-500/20">{hotAlerts.counts.urgent} URGENT</span>}
                      {hotAlerts.counts?.attention > 0 && <span className="px-1.5 py-0.5 rounded text-[8px] font-black bg-amber-500/20 text-amber-300 border border-amber-500/20">{hotAlerts.counts.attention} ATTENTION</span>}
                      {hotAlerts.counts?.opportunite > 0 && <span className="px-1.5 py-0.5 rounded text-[8px] font-black bg-cyan-500/20 text-cyan-300 border border-cyan-500/20">{hotAlerts.counts.opportunite} OPPORTUNITE</span>}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    {hotAlerts.hot.map((h, i) => {
                      const priorityColors = {
                        URGENT: { bg: "bg-red-500/8", border: "border-red-500/25", badge: "bg-red-500/20 text-red-300 border-red-500/25", dot: "bg-red-400" },
                        ATTENTION: { bg: "bg-amber-500/5", border: "border-amber-500/20", badge: "bg-amber-500/20 text-amber-300 border-amber-500/25", dot: "bg-amber-400" },
                        OPPORTUNITE: { bg: "bg-cyan-500/5", border: "border-cyan-500/20", badge: "bg-cyan-500/20 text-cyan-300 border-cyan-500/25", dot: "bg-cyan-400" },
                      };
                      const c = priorityColors[h.priority] || priorityColors.ATTENTION;
                      return (
                        <div key={`hot-${h.ticker}-${i}`}
                          className={`p-2.5 rounded-lg ${c.bg} border ${c.border} hover:brightness-125 transition-all cursor-pointer group`}
                          onClick={() => { onAnalyze?.(h.ticker); onClose(); }}
                          data-testid={`hot-alert-${h.ticker}`}>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-black text-slate-500 w-4">{i + 1}.</span>
                            <div className={`w-1.5 h-1.5 rounded-full ${c.dot} shrink-0`} />
                            <span className="text-sm font-black text-white group-hover:text-cyan-300 transition-colors">{h.ticker}</span>
                            <span className={`px-1.5 py-0.5 rounded text-[8px] font-black border ${c.badge}`}>{h.priority}</span>
                            <span className="px-1.5 py-0.5 rounded text-[8px] font-bold bg-slate-700/40 text-slate-400 border border-slate-600/20">{h.type}</span>
                            <ArrowRight className="w-3 h-3 text-slate-600 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                          <div className="ml-6 mt-1">
                            <div className="text-[10px] text-slate-400 leading-relaxed">{h.detail}</div>
                            {h.action && <div className="text-[9px] text-slate-500 mt-0.5 font-medium">{h.action}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {/* ============================================ */}
              {/* SECTION 1: A ACHETER MAINTENANT (Triggered)  */}
              {/* ============================================ */}
              {triggered.length > 0 && (
                <div data-testid="buy-now-section">
                  <div className="flex items-center gap-2 mb-3 p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                    <ShoppingCart className="w-4 h-4 text-emerald-400" />
                    <span className="text-xs font-black text-emerald-300 uppercase tracking-wider">A Acheter Maintenant</span>
                    <span className="ml-auto px-2 py-0.5 rounded-full text-[10px] font-black bg-emerald-500/20 text-emerald-300">
                      {triggered.length} action{triggered.length > 1 ? "s" : ""}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {triggered.map((a, i) => (
                      <div key={`trig-${a.ticker}-${i}`} className="p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/20 hover:border-emerald-500/40 transition-all cursor-pointer group" onClick={() => { onAnalyze?.(a.ticker); onClose(); }} data-testid={`buy-now-alert-${a.ticker}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-emerald-400" />
                            <span className="text-base font-black text-white group-hover:text-emerald-300 transition-colors">{a.ticker}</span>
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/20">PRIX RECO ATTEINT</span>
                          </div>
                          <button onClick={(e) => { e.stopPropagation(); handleDismiss(a); }} className="p-1 rounded hover:bg-slate-700 text-slate-600 hover:text-white transition-colors opacity-0 group-hover:opacity-100" data-testid={`dismiss-alert-${a.ticker}`}>
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        {/* Price info */}
                        <div className="grid grid-cols-3 gap-2 mb-2">
                          <div className="p-1.5 rounded-lg bg-slate-800/60 text-center">
                            <div className="text-[8px] text-slate-500 uppercase">Prix Reco</div>
                            <div className="text-xs font-bold text-amber-300 font-mono">{curr(a.ticker)}{a.target_price}</div>
                          </div>
                          <div className="p-1.5 rounded-lg bg-slate-800/60 text-center">
                            <div className="text-[8px] text-slate-500 uppercase">Atteint a</div>
                            <div className="text-xs font-bold text-white font-mono">{curr(a.ticker)}{a.triggered_price}</div>
                          </div>
                          <div className="p-1.5 rounded-lg bg-slate-800/60 text-center">
                            <div className="text-[8px] text-slate-500 uppercase">Maintenant</div>
                            <div className="text-xs font-bold text-white font-mono">{a.current_price ? `${curr(a.ticker)}${a.current_price}` : "--"}</div>
                          </div>
                        </div>
                        {/* Perf since trigger */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {a.perf_since_trigger != null && (
                              <span className={`text-sm font-black font-mono ${a.perf_since_trigger >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                                {a.perf_since_trigger >= 0 ? "+" : ""}{a.perf_since_trigger.toFixed(1)}% depuis signal
                              </span>
                            )}
                          </div>
                          <TimeAgo iso={a.triggered_at} />
                        </div>
                        {a.note && <div className="mt-1.5 text-[10px] text-slate-500 truncate">{a.note}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ============================================ */}
              {/* SECTION 2: TOP ALERTES PROCHES (< 5%)       */}
              {/* ============================================ */}
              {closeAlerts.length > 0 && (
                <div data-testid="close-alerts-section">
                  <div className="flex items-center gap-2 mb-3 p-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <Flame className="w-4 h-4 text-amber-400" />
                    <span className="text-xs font-black text-amber-300 uppercase tracking-wider">Top Alertes Proches</span>
                    <span className="text-[9px] text-slate-500 ml-1">{"<"} 5% du target</span>
                    <span className="ml-auto px-2 py-0.5 rounded-full text-[10px] font-black bg-amber-500/20 text-amber-300">
                      {closeAlerts.length}
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    {closeAlerts.map((a, i) => {
                      const isVeryClose = a.distance_pct != null && Math.abs(a.distance_pct) < 2;
                      return (
                        <div key={`close-${a.ticker}-${i}`} className={`p-3 rounded-xl border transition-all cursor-pointer group ${isVeryClose ? "bg-emerald-500/5 border-emerald-500/20 hover:border-emerald-500/40" : "bg-amber-500/5 border-amber-500/15 hover:border-amber-500/30"}`} onClick={() => { onAnalyze?.(a.ticker); onClose(); }} data-testid={`close-alert-${a.ticker}`}>
                          <div className="flex items-center justify-between mb-1.5">
                            <div className="flex items-center gap-2">
                              <Crosshair className={`w-3.5 h-3.5 ${isVeryClose ? "text-emerald-400 animate-pulse" : "text-amber-400"}`} />
                              <span className="text-sm font-black text-white group-hover:text-amber-300 transition-colors">{a.ticker}</span>
                              <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold border ${a.signal === "ENTRER" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/20" : a.signal === "CUSTOM" ? "bg-amber-500/15 text-amber-300 border-amber-500/20" : "bg-slate-700/50 text-slate-300 border-slate-600/30"}`}>{a.signal}</span>
                              {isVeryClose && <span className="text-[9px] text-emerald-400 font-bold animate-pulse">IMMINENT</span>}
                            </div>
                            <button onClick={(e) => { e.stopPropagation(); handleRemove(a.ticker); }} className="p-1 rounded hover:bg-red-500/15 text-slate-600 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100" data-testid={`remove-alert-${a.ticker}`}>
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                          <div className="space-y-1.5">
                            <div className="flex items-center justify-between text-[10px]">
                              <span className="text-slate-400">
                                Actuel: <span className="text-white font-mono font-bold">{a.current_price ? `${curr(a.ticker)}${a.current_price}` : "--"}</span>
                              </span>
                              <span className="text-slate-400">
                                Cible: <span className="text-amber-300 font-mono font-bold">{curr(a.ticker)}{a.target_price}</span>
                              </span>
                            </div>
                            <DistanceBar pct={a.distance_pct} />
                          </div>
                          {a.note && <div className="mt-1.5 text-[10px] text-slate-500 truncate">{a.note}</div>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ============================================ */}
              {/* SECTION 3: EN SURVEILLANCE (>= 5%)          */}
              {/* ============================================ */}
              {surveillanceAlerts.length > 0 && (
                <div data-testid="surveillance-section">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="w-3.5 h-3.5 text-slate-400" />
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">En Surveillance</span>
                    <span className="text-[9px] text-slate-600">{surveillanceAlerts.length} alerte{surveillanceAlerts.length > 1 ? "s" : ""}</span>
                  </div>
                  <div className="space-y-1.5">
                    {surveillanceAlerts.map((a, i) => (
                      <div key={`surv-${a.ticker}-${i}`} className="p-3 rounded-xl bg-slate-800/30 border border-slate-700/30 hover:border-slate-600/50 transition-all cursor-pointer group" onClick={() => { onAnalyze?.(a.ticker); onClose(); }} data-testid={`surveillance-alert-${a.ticker}`}>
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2">
                            <Bell className="w-3 h-3 text-slate-500" />
                            <span className="text-sm font-bold text-slate-300 group-hover:text-white transition-colors">{a.ticker}</span>
                            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold border ${a.signal === "ENTRER" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/20" : a.signal === "CUSTOM" ? "bg-amber-500/15 text-amber-300 border-amber-500/20" : "bg-slate-700/50 text-slate-300 border-slate-600/30"}`}>{a.signal}</span>
                          </div>
                          <button onClick={(e) => { e.stopPropagation(); handleRemove(a.ticker); }} className="p-1 rounded hover:bg-red-500/15 text-slate-600 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100" data-testid={`remove-surv-alert-${a.ticker}`}>
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between text-[10px]">
                            <span className="text-slate-500">
                              Actuel: <span className="text-slate-300 font-mono">{a.current_price ? `${curr(a.ticker)}${a.current_price}` : "--"}</span>
                            </span>
                            <span className="text-slate-500">
                              Cible: <span className="text-slate-300 font-mono">{curr(a.ticker)}{a.target_price}</span>
                            </span>
                          </div>
                          <DistanceBar pct={a.distance_pct} />
                        </div>
                        {a.note && <div className="mt-1 text-[10px] text-slate-600 truncate">{a.note}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Empty state */}
              {active.length === 0 && triggered.length === 0 && (
                <div className="text-center py-16" data-testid="alerts-empty">
                  <div className="w-14 h-14 rounded-2xl bg-slate-800/60 border border-slate-700/40 flex items-center justify-center mx-auto mb-3">
                    <Bell className="w-7 h-7 text-slate-600" />
                  </div>
                  <p className="text-sm text-slate-400 font-medium">Aucune alerte</p>
                  <p className="text-[11px] text-slate-600 mt-1 max-w-[240px] mx-auto">
                    Analysez une action dans Algo Trading et cliquez "Alerter", ou utilisez le + ci-dessus
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer summary */}
        {(active.length > 0 || triggered.length > 0) && !loading && (
          <div className="p-3 border-t border-slate-700/50 bg-slate-800/30 shrink-0">
            <div className="flex items-center justify-between text-[10px] text-slate-400">
              <div className="flex items-center gap-3">
                {triggered.length > 0 && (
                  <span className="flex items-center gap-1 text-emerald-400 font-bold">
                    <CheckCircle className="w-3 h-3" /> {triggered.length} a acheter
                  </span>
                )}
                {closeAlerts.length > 0 && (
                  <span className="flex items-center gap-1 text-amber-400 font-bold">
                    <Flame className="w-3 h-3" /> {closeAlerts.length} proche{closeAlerts.length > 1 ? "s" : ""}
                  </span>
                )}
                <span>{surveillanceAlerts.length} en veille</span>
              </div>
              {closeAlerts.length > 0 && (
                <span className="text-amber-300 font-bold">
                  {closeAlerts[0].ticker} a {Math.abs(closeAlerts[0].distance_pct || 0).toFixed(1)}%
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
